import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Combobox } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import ResultsMenu from './ResultsMenu';

function GlobalSearch(): JSX.Element {
    const isMac = navigator.userAgent.toUpperCase().includes('MAC');
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [isFocused, setIsFocused] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const navigate = useNavigate();

    const placeholder = isFocused ? 'Search groups and entities' : '';

    function focusInput(): void {
        inputRef.current?.focus();
    }

    function resetSearch(): void {
        setDebouncedSearch('');
        setSearch('');
        setIsFocused(false);
        inputRef.current?.blur();
    }

    const handleShortcutKey = useCallback(
        function handleShortcutKey(event: KeyboardEvent): void {
            if (!event || !event.key) {
                return;
            }
            const keyPressed = event.key.toLowerCase() === 'k';

            // Mac listen for cmd + K
            if (isMac && event.metaKey && keyPressed) {
                event.preventDefault();
                focusInput();
            }
            // Non-mac (Windows) listen for ctrl + k
            else if (!isMac && event.ctrlKey && keyPressed) {
                event.preventDefault();
                focusInput();
            }
        },
        [isMac]
    );

    /**
     * Whenever the value changes don't fire setDebouncedSearch until the timeout has completed.
     * every time a new value is entered; reset the timeout
     */
    useEffect(() => {
        if (!search) {
            setDebouncedSearch('');
            return;
        }

        let timeout: NodeJS.Timeout;

        if (search !== undefined) {
            timeout = setTimeout(() => setDebouncedSearch(search), 250);
        }
        return (): void => timeout && clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        document.addEventListener('keydown', handleShortcutKey);
        return (): void => document.removeEventListener('keydown', handleShortcutKey);
    }, [handleShortcutKey]);

    return (
        <div
            data-intercom-target="header-search-bar"
            className={`absolute  -translate-x-full z-50 bg-indigo-800 rounded-md ${isFocused ? 'shadow-lg border border-white' : ''}`}
        >
            <Combobox
                value={search || ''}
                onChange={(url): void => {
                    navigate(url);
                    resetSearch();
                }}
            >
                <div
                    className="relative"
                    onKeyDown={(e): void => {
                        if (e.key === 'Escape') {
                            resetSearch();
                        }
                    }}
                >
                    <div className="h-8 dark:bg-white/[0.05]  px-2 w-max flex items-center gap-1">
                        <MagnifyingGlassIcon
                            className="text-white dark:text-gray-200 h-5 w-5 shrink-0 cursor-pointer"
                            onClick={focusInput}
                        />
                        <motion.div initial={false} animate={{ width: isFocused ? 375 : 0 }} transition={{ duration: 0.15 }}>
                            <Combobox.Input
                                ref={inputRef}
                                className="bg-transparent w-full border-0 outline-none ring-0 text-white text-sm flex-1 px-1 placeholder:text-white/60"
                                placeholder={placeholder}
                                onChange={(event): void => setSearch(event.target.value.trim())}
                                onFocus={(): void => setIsFocused(true)}
                                onBlur={resetSearch}
                                onKeyDown={(e: React.KeyboardEvent): void => {
                                    if (e.key === 'Escape') {
                                        resetSearch();
                                    }
                                }}
                            />
                        </motion.div>
                        <span className="text-white dark:text-gray-200 text-sm bg-indigo-900 dark:bg-white/[0.05] dark:border-white/[0.05] py-0.5 px-2 rounded ml-auto shrink-0 items-center pointer-events-none">
                            {isMac && <kbd className="font-sans">⌘</kbd>}
                            {!isMac && <kbd className="font-sans">ctrl + </kbd>}
                            <kbd className="font-sans">K</kbd>
                        </span>
                    </div>
                    {debouncedSearch && <ResultsMenu search={debouncedSearch} onNavigate={resetSearch} />}
                </div>
            </Combobox>
        </div>
    );
}

export default GlobalSearch;
